<template>
    <v-dialog v-model="dialog" max-width="800px" persistent>
        <v-card :loading="loading">
            <v-card-title class="text-h5">
                <v-btn
                    class="mr-3"
                    depressed
                    color="primary"
                    icon
                    rounded
                    @click="closeDialog"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                {{ title }}
                <v-spacer></v-spacer>
            </v-card-title>

            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <v-row>
                        <v-col cols="6">
                            <v-menu
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                                v-model="requestDateMenu"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="formData.requestDate"
                                        label="Request Date*"
                                        prepend-icon="mdi-calendar"
                                        :disabled="!isFormEditable"
                                        v-bind="attrs"
                                        v-on="on"
                                        required
                                        hide-details
                                        class=""
                                        :rules="[rules.required]"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="formData.requestDate"
                                    @input="requestDateMenu = false"
                                    :min="minDate"
                                ></v-date-picker>
                            </v-menu>
                        </v-col>

                        <v-col cols="6">
                            <v-select
                                v-model="formData.requiresPredesign"
                                :items="yesNoOptions"
                                item-text="text"
                                item-value="value"
                                prepend-icon="mdi-cog"
                                label="Requires Predesign*"
                                :disabled="!isFormEditable"
                                required
                                hide-details
                            ></v-select>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="6">
                            <v-menu
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                                v-model="dueDateMenu"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="formData.dueDate"
                                        label="Due Date*"
                                        prepend-icon="mdi-calendar"
                                        :disabled="!isFormEditable"
                                        v-bind="attrs"
                                        v-on="on"
                                        required
                                        hide-details
                                        class=""
                                        :rules="[rules.required]"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="formData.dueDate"
                                    @input="dueDateMenu = false"
                                    :min="minDate"
                                ></v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col cols="6">
                            <v-autocomplete
                                v-model="formData.requestedBy"
                                :items="users"
                                item-text="name"
                                item-value="id"
                                prepend-icon="mdi-account"
                                label="Requested By*"
                                :disabled="!isFormEditable"
                                required
                                :rules="[rules.required]"
                                hide-details
                            >
                                <template slot="item" slot-scope="{ item }">
                                    <v-row>
                                        <p class="ma-0 ml-1 pa-0">
                                            {{ item.name }}
                                        </p>
                                        <h5
                                            class="grey--text ma-0 ml-1 mt-1 pa-0"
                                        >
                                            ({{ item.role }})
                                        </h5>
                                    </v-row>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model="formData.targetPrice"
                                prepend-icon="mdi-currency-usd"
                                label="Target Price"
                                type="number"
                                step="0.01"
                                :disabled="!isFormEditable"
                                hide-details
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="formData.projectId"
                                prepend-icon="mdi-folder"
                                label="Project Id *"
                                :disabled="!isFormEditable"
                                :rules="[rules.required]"
                                required
                                hide-details
                                :readonly="
                                    leadStatus !== undefined ||
                                        quoteStatus !== undefined
                                "
                                @change="updateProject"
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="6">
                            <v-menu
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                                v-model="drawingsSetDateMenu"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="formData.drawingsSetDate"
                                        label="Drawings Set Date"
                                        prepend-icon="mdi-calendar"
                                        :disabled="!isFormEditable"
                                        v-bind="attrs"
                                        v-on="on"
                                        hide-details
                                        class=""
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="formData.drawingsSetDate"
                                    @input="drawingsSetDateMenu = false"
                                ></v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="formData.coNumber"
                                prepend-icon="mdi-tag"
                                label="CO#"
                                :disabled="!isFormEditable"
                                :rules="[rules.required]"
                                readonly
                                required
                                hide-details
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="6">
                            <v-select
                                v-model="formData.sendToPmClient"
                                :items="options"
                                prepend-icon="mdi-send"
                                label="Send to PM Client"
                                :disabled="!isFormEditable"
                                hide-details
                            ></v-select>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="formData.finishNameAndUc"
                                prepend-icon="mdi-palette"
                                label="Finish Name and UC"
                                :disabled="!isFormEditable"
                                hide-details
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model="formData.keyDrawings"
                                prepend-icon="mdi-drawing"
                                label="Key Drawings"
                                :disabled="!isFormEditable"
                                hide-details
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="formData.subject"
                                prepend-icon="mdi-text-long"
                                label="Subject"
                                :disabled="!isFormEditable"
                                :rules="[rules.required, rules.maxLength20]"
                                maxlength="20"
                                counter="20"
                                required
                                hide-details
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="leadName"
                                prepend-icon="mdi-file-document-edit"
                                label="Lead Name"
                                readonly
                                hide-details
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <label class="v-label theme--light"
                                >Request Description</label
                            >
                            <div class="mt-2">
                                <vue-editor
                                    :editor-options="editorOptions"
                                    v-model="formData.requestDescription"
                                />
                            </div>
                        </v-col>
                    </v-row>
                </v-form>

                <small>* indicates required field</small>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="closeDialog">
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    :loading="loading"
                    :disabled="!valid || !isFormEditable"
                    @click="saveRequest"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import API from '@/services/api'
import { VueEditor } from 'vue2-editor'
import { auth } from '@/services/firebase'

export default {
    name: 'RequestFormDialog',
    components: {
        VueEditor,
    },
    props: {
        title: {
            type: String,
            default: 'Change Order Information',
        },
        value: {
            type: Boolean,
            default: false,
        },
        existingData: {
            type: Object,
            default: null,
        },
        isEditing: {
            type: Boolean,
            default: false,
        },
        leadStatus: {
            type: String,
            default: undefined,
        },
        quoteStatus: {
            type: String,
            default: undefined,
        },
        changeOrderId: {
            type: String,
            default: null,
        },
        isArchived: {
            type: Boolean,
            default: false,
        },
        forceEditable: {
            type: Boolean,
            default: false,
        },
        projectName: {
            type: String,
            default: '',
        },
        codeOrder: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            valid: false,
            users: [],
            requestDateMenu: false,
            dueDateMenu: false,
            drawingsSetDateMenu: false,
            yesNoOptions: [
                { text: 'Yes', value: true },
                { text: 'No', value: false },
            ],
            options: ['Client', 'PM'],
            rules: {
                required: v => !!v || 'Required',
                maxLength20: v =>
                    !v || v.length <= 20 || 'Maximum 20 characters',
            },
            formData: {
                requestDate: '',
                requiresPredesign: null,
                dueDate: '',
                requestedBy: null,
                targetPrice: null,
                projectId: '',
                drawingsSetDate: '',
                coNumber: '',
                sendToPmClient: null,
                finishNameAndUc: '',
                keyDrawings: '',
                requestDescription: '',
                subject: '',
                createdOn: null,
            },
            minDate: new Date(
                Date.now() - new Date().getTimezoneOffset() * 60000
            )
                .toISOString()
                .substr(0, 10),
            currentDate: new Date(
                Date.now() - new Date().getTimezoneOffset() * 60000
            )
                .toISOString()
                .substr(0, 10),
        }
    },
    computed: {
        isFormEditable() {
            // If forceEditable is true, always allow editing
            if (this.forceEditable) {
                return true
            }

            // Use quoteStatus if available (for quotes), otherwise use leadStatus (for leads)
            const status =
                this.quoteStatus !== undefined
                    ? this.quoteStatus
                    : this.leadStatus

            // For quotes: only editable if status is OPEN or APPROVAL and not archived
            if (this.quoteStatus !== undefined) {
                return (
                    (status === 'OPEN' || status === 'APPROVAL') &&
                    !this.isArchived
                )
            }

            // For leads: editable if inProgress, onHold, or undefined
            return (
                status === 'inProgress' ||
                status === 'onHold' ||
                status === undefined
            )
        },
        editorOptions() {
            return {
                theme: 'snow',
                readOnly: !this.isFormEditable,
                modules: {
                    toolbar: this.isFormEditable
                        ? [
                              ['bold', 'italic', 'underline'], // estilos básicos
                              [{ list: 'bullet' }], // viñetas
                              [{ list: 'ordered' }], // listas numeradas
                              [{ color: [] }], // selector de color
                              ['link', 'image'], // enlaces e imágenes
                              ['clean'], // limpiar formato
                          ]
                        : false,
                },
            }
        },
        leadName() {
            const projectName = this.formData.projectId || ''
            const coNumber = this.formData.coNumber || ''
            const subject = this.formData.subject || ''

            if (projectName && coNumber && subject) {
                return `${projectName}-CO#${coNumber}-${subject}`
            }
            return ''
        },
    },
    watch: {
        value(newVal) {
            this.dialog = newVal
            if (newVal && this.isEditing && this.existingData) {
                this.loadExistingData()
            }
            // set the project name if it is not set
            if (newVal && !this.formData.projectId && this.projectName) {
                this.formData.projectId = this.projectName
            }
            // set the co number if it is not set
            if (newVal && !this.formData.coNumber && this.codeOrder) {
                this.formData.coNumber = this.codeOrder
            }
            // set the request date if it is not set
            if (newVal && !this.formData.requestDate && this.currentDate) {
                this.formData.requestDate = this.currentDate
            }
            // set the due date if it is not set
            if (newVal && !this.formData.dueDate && this.currentDate) {
                this.formData.dueDate = this.currentDate
            }
        },
        dialog(newVal) {
            this.$emit('input', newVal)
        },
    },
    async mounted() {
        try {
            const {
                data: { users },
            } = await API.getLiteUsers()
            this.users = users
            this.formData.coNumber = this.codeOrder
            if (!this.existingData) {
                const currentUser = auth().currentUser.uid
                this.formData.requestedBy = this.users.find(
                    user => user.id === currentUser
                )
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['addUsersAction']),

        updateProject() {
            if (!this.leadStatus && !this.quoteStatus) {
                this.$emit('replaceProjectName', this.formData.projectId)
            }
        },

        closeDialog() {
            this.dialog = false
            this.resetForm()
            this.$emit('cancel')
        },

        loadExistingData() {
            if (this.existingData) {
                this.formData = {
                    requestDate: this.existingData.requestDate || '',
                    requiresPredesign: this.existingData.requiresPredesign,
                    dueDate: this.existingData.dueDate || '',
                    requestedBy: this.existingData.requestedBy,
                    targetPrice: this.existingData.targetPrice,
                    projectId: this.existingData.projectId || '',
                    drawingsSetDate: this.existingData.drawingsSetDate || '',
                    coNumber: this.existingData.coNumber || '',
                    sendToPmClient: this.existingData.sendToPmClient,
                    finishNameAndUc: this.existingData.finishNameAndUc || '',
                    keyDrawings: this.existingData.keyDrawings || '',
                    requestDescription:
                        this.existingData.requestDescription || '',
                    subject: this.existingData.subject || '',
                    createdOn: this.existingData.createdOn || null,
                }
            }
        },

        resetForm() {
            this.formData = {
                requestDate: '',
                requiresPredesign: null,
                dueDate: '',
                requestedBy: null,
                targetPrice: null,
                projectId: '',
                drawingsSetDate: '',
                coNumber: '',
                sendToPmClient: null,
                finishNameAndUc: '',
                keyDrawings: '',
                requestDescription: '',
                subject: '',
                createdOn: null,
            }
            this.$refs.form.reset()
        },

        async saveRequest() {
            try {
                this.loading = true

                // Validate form
                if (!this.$refs.form.validate()) {
                    return
                }

                // Prepare data for API call
                const requestData = {
                    ...this.formData,
                    leadName: this.leadName,
                    requestedBy:
                        typeof this.formData.requestedBy === 'object'
                            ? this.formData.requestedBy.id
                            : this.formData.requestedBy,
                    targetPrice: this.formData.targetPrice
                        ? parseFloat(this.formData.targetPrice)
                        : null,
                    id: this.changeOrderId, // Include the change order ID
                }
                // Emit the data to parent component
                this.$emit('save', requestData)

                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped></style>
